import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import StoryPlate from './StoryPlate.jsx';
import { CHAPTERS, CHAPTERS_BY_ID } from '../data/story.js';
import { VERSES_BY_ID } from '../data/verses.js';
import { playCorrect, playWrong } from '../lib/sfx.js';

/**
 * A single story chapter, read one beat at a time.
 *
 * The stages are: plate → six scenes → anchor verse → three or four questions
 * → out. Paging rather than scrolling is deliberate; a wall of prose is the
 * thing people bounce off, and one paragraph at a time keeps it closer to a
 * lesson than to an article.
 *
 * ── Why the questions come one at a time ────────────────────────────────────
 *
 * A page of four questions is a test. One question, answered, explained, and
 * then the next is a conversation, and it means the explanation is read while
 * the question is still live rather than skimmed at the end alongside three
 * others. It also lets the set escalate — recall, then comprehension, then what
 * it means — which only works if they are met in order.
 *
 * The chapter completes when the last one is answered, right or wrong. Locking
 * progress behind a perfect score would trap somebody on chapter three of
 * twenty-eight, and the point is that they read the story. Getting them all
 * right is worth extra XP instead, which is the carrot doing the work the stick
 * would do badly.
 *
 * Scene text supports *emphasis* — quoted scripture inside the prose is marked
 * that way so it can be set in the serif face without the data file needing to
 * carry markup.
 */
export default function StoryChapter({
  chapterId,
  isBookmarked,
  onComplete,
  onToggleBookmark,
  onAsk,
  onExit,
  onNext,
}) {
  const chapter = CHAPTERS_BY_ID[chapterId];
  const [step, setStep] = useState(0);
  const [choices, setChoices] = useState([]);
  const bodyRef = useRef(null);

  const sceneCount = chapter?.scenes.length ?? 0;
  const questions = chapter?.questions ?? [];

  // step 0 is the plate, then the scenes, then the verse, then one step per
  // question. Naming the boundaries once keeps the arithmetic out of the JSX.
  const firstScene = 1;
  const verseStep = firstScene + sceneCount;
  const firstQuestion = verseStep + 1;
  const totalSteps = firstQuestion + questions.length;

  const onPlate = step === 0;
  const onScene = step >= firstScene && step < verseStep;
  const onVerseStep = step === verseStep;
  const questionIndex = step - firstQuestion;
  const question = questionIndex >= 0 ? questions[questionIndex] : null;

  // A new chapter starts at the beginning, even if the component is reused.
  useEffect(() => {
    setStep(0);
    setChoices([]);
  }, [chapterId]);

  // Each beat should start at the top; without this, paging forward on a phone
  // leaves you halfway down the next scene.
  useEffect(() => {
    bodyRef.current?.scrollTo?.({ top: 0 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const verse = chapter ? VERSES_BY_ID[chapter.verseId] : null;
  const choice = question ? (choices[questionIndex] ?? null) : null;
  const answered = choice !== null;
  const correct = answered && choice === question?.answer;
  const lastQuestion = questionIndex === questions.length - 1;

  const nextChapterId = useMemo(() => {
    const i = CHAPTERS.findIndex((c) => c.id === chapterId);
    return i >= 0 && i + 1 < CHAPTERS.length ? CHAPTERS[i + 1].id : null;
  }, [chapterId]);

  const choose = useCallback(
    (optionIndex) => {
      if (answered || !question) return;
      const right = optionIndex === question.answer;

      const nextChoices = [...choices];
      nextChoices[questionIndex] = optionIndex;
      setChoices(nextChoices);

      // Same as the quiz: fired from the click so the AudioContext is born
      // inside a user gesture.
      if (right) playCorrect();
      else playWrong();

      // Recorded once, on the last answer. `correct` here means a clean run —
      // every question right — because that is what the bonus XP is for.
      if (questionIndex === questions.length - 1) {
        const clean = questions.every((q, i) => nextChoices[i] === q.answer);
        onComplete?.(chapter.id, clean);
      }
    },
    [answered, choices, questionIndex, question, questions, chapter, onComplete],
  );

  if (!chapter) return null;

  const progressPct = Math.round(((step + (answered ? 1 : 0)) / totalSteps) * 100);
  const score = questions.filter((q, i) => choices[i] === q.answer).length;

  return (
    <section className="screen story-read">
      <div className="story-read__bar">
        <button type="button" className="icon-btn icon-btn--sm" onClick={onExit}>
          <Icon name="arrow" size={15} className="icon--flip" />
          The Story
        </button>
        <span className="story-read__count">
          Chapter {chapter.order} of {CHAPTERS.length}
        </span>
      </div>

      <div className="deck-progress">
        <div className="deck-progress__track">
          <div className="deck-progress__fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="deck-progress__label">{chapter.era}</span>
      </div>

      <header className="story-read__head">
        <span className="story-read__act">{chapter.actTitle}</span>
        <h2>{chapter.title}</h2>
        <p className="story-read__where">{chapter.where}</p>
      </header>

      <div className="story-read__body" ref={bodyRef}>
        {onPlate ? (
          <article className="scene scene--plate" key="plate">
            <StoryPlate chapter={chapter} />
            {chapter.intro ? <p className="plate__intro">{chapter.intro}</p> : null}
            {/* The opening action belongs under the picture, centred, where the
                eye already is — not in the footer bar with Back, which is where
                every other step's Continue lives. This is a cover, not a step. */}
            <button
              type="button"
              className="btn btn--primary btn--lg plate__begin"
              onClick={() => setStep(1)}
            >
              Begin
            </button>
          </article>
        ) : null}

        {onScene ? (
          <article className="scene" key={step}>
            {splitScene(chapter.scenes[step - firstScene]).map((para, i) => (
              <p key={i} className={`scene__text${i === 0 ? ' scene__text--lead' : ''}`}>
                {renderScene(para)}
              </p>
            ))}
            <span className="scene__pips" aria-hidden="true">
              {chapter.scenes.map((_, i) => (
                <span key={i} className={i === step - firstScene ? 'is-on' : undefined} />
              ))}
            </span>
          </article>
        ) : null}

        {onVerseStep && verse ? (
          <article className="scene scene--verse" key="verse">
            <span className="scene__label">The verse to carry</span>
            <blockquote className="scripture">{verse.text}</blockquote>
            <cite className="citation">
              {verse.ref} · {verse.theme}
            </cite>
            <div className="scene__tools">
              <button
                type="button"
                className={`icon-btn icon-btn--sm${isBookmarked ? ' is-on' : ''}`}
                onClick={() => onToggleBookmark(verse.id)}
                aria-pressed={isBookmarked}
              >
                <Icon name="ribbon" size={15} />
                {isBookmarked ? 'Saved' : 'Save'}
              </button>
              <button type="button" className="icon-btn icon-btn--sm" onClick={() => onAsk(verse)}>
                <Icon name="dove" size={15} />
                Ask about this
              </button>
            </div>
          </article>
        ) : null}

        {question ? (
          <article className="scene scene--question" key={question.id}>
            <span className="scene__label">
              Question {questionIndex + 1} of {questions.length}
            </span>
            <h3 className="question__text">{question.question}</h3>

            <ul className="options">
              {question.options.map((option, i) => {
                const isRight = i === question.answer;
                const isChosen = i === choice;
                let className = 'option';
                if (answered && isRight) className += ' is-correct';
                else if (answered && isChosen) className += ' is-wrong';
                else if (answered) className += ' is-dim';

                return (
                  <li key={option}>
                    <button
                      type="button"
                      className={className}
                      onClick={() => choose(i)}
                      disabled={answered}
                    >
                      <span className="option__marker" aria-hidden="true">
                        {answered && isRight ? (
                          <Icon name="check" size={14} />
                        ) : answered && isChosen ? (
                          <Icon name="close" size={13} />
                        ) : (
                          String.fromCharCode(65 + i)
                        )}
                      </span>
                      <span className="option__label">{option}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {answered ? (
              <div className={`explain${correct ? ' is-correct' : ' is-wrong'}`}>
                <strong>{correct ? 'Correct' : 'Not quite'}</strong>
                <p>{question.explanation}</p>
              </div>
            ) : null}

            {answered && lastQuestion ? (
              <>
                <p className="story-read__score">
                  {score} of {questions.length} right
                </p>
                <div className="story-read__takeaway">
                  <Icon name="scroll" size={16} />
                  <p>{chapter.takeaway}</p>
                </div>
              </>
            ) : null}
          </article>
        ) : null}
      </div>

      <div className="story-read__foot">
        {step > 0 ? (
          <button type="button" className="btn btn--ghost" onClick={() => setStep((s) => s - 1)}>
            Back
          </button>
        ) : null}

        {onPlate ? null : !question ? (
          <button type="button" className="btn btn--primary" onClick={() => setStep((s) => s + 1)}>
            {onVerseStep ? 'To the questions' : 'Continue'}
          </button>
        ) : answered && !lastQuestion ? (
          <button type="button" className="btn btn--primary" onClick={() => setStep((s) => s + 1)}>
            Next question
          </button>
        ) : answered ? (
          nextChapterId ? (
            <button type="button" className="btn btn--primary" onClick={() => onNext(nextChapterId)}>
              Next chapter
            </button>
          ) : (
            <button type="button" className="btn btn--primary" onClick={onExit}>
              Finish the story
            </button>
          )
        ) : null}
      </div>
    </section>
  );
}

/**
 * Break a scene into short paragraphs.
 *
 * Ninety words arriving as one unbroken block is the thing people bounce off,
 * and it is a formatting problem before it is a writing one — the same words in
 * three short paragraphs are visibly easier to start. So the break is done here
 * rather than by editing 168 scenes: every chapter gets it, including any
 * written later by someone who never reads this file.
 *
 * Split on blank lines where the text has them, so a scene can say where it
 * wants to break. Where it does not, fall back to grouping sentences two at a
 * time, which lands close to where a writer would have broken it anyway.
 *
 * Sentence-splitting on punctuation is crude and gets abbreviations wrong. It
 * is safe here because the consequence of a bad split is a paragraph in a
 * slightly odd place, and because scripture quotations — the one place a full
 * stop reliably appears mid-sentence — are wrapped in *emphasis* markers and
 * skipped.
 */
function splitScene(text) {
  const explicit = text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (explicit.length > 1) return explicit;

  const sentences = [];
  let buffer = '';
  let inQuote = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    buffer += ch;

    // *...* wraps quoted scripture, which contains its own full stops and
    // question marks. Treating a marked span as atomic is the whole reason
    // this is a scanner and not a regex.
    if (ch === '*') inQuote = !inQuote;
    if (inQuote || (ch !== '.' && ch !== '!' && ch !== '?')) continue;

    let j = i + 1;
    while (j < text.length && /["'\u2019)\]]/.test(text[j])) {
      buffer += text[j];
      j += 1;
    }
    // A full stop not followed by a space is an abbreviation or a decimal,
    // not the end of a sentence.
    if (j < text.length && !/\s/.test(text[j])) {
      i = j - 1;
      continue;
    }
    sentences.push(buffer.trim());
    buffer = '';
    while (j < text.length && /\s/.test(text[j])) j += 1;
    i = j - 1;
  }
  if (buffer.trim()) sentences.push(buffer.trim());

  if (sentences.length < 4) return [text.trim()];

  const paragraphs = [];
  for (let i = 0; i < sentences.length; i += 2) {
    paragraphs.push(sentences.slice(i, i + 2).join(' '));
  }
  // A one-sentence orphan at the end reads as a mistake rather than a beat.
  if (paragraphs.length > 1 && paragraphs.at(-1).split(/\s+/).length < 8) {
    const orphan = paragraphs.pop();
    paragraphs[paragraphs.length - 1] += ' ' + orphan;
  }
  return paragraphs;
}

/**
 * Split scene prose on *emphasis* markers so quoted scripture can be set in the
 * serif face. Deliberately the smallest possible markup: the data file holds
 * prose, not HTML, and nothing is ever injected as raw markup.
 */
function renderScene(text) {
  return text.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith('*') && part.endsWith('*') && part.length > 2 ? (
      <em key={i} className="scene__quote">
        {part.slice(1, -1)}
      </em>
    ) : (
      part
    ),
  );
}
